import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * Polls the pending-approvals count for the current user's role.
 * - role="client":    `/api/client/approvals/count`  → { issues, extras, total }
 * - role="admin":     `/api/admin/approvals/count`   → { pending_total, decided_24h_total, ... }
 *
 * Returns a single `{ total, ... }` object so callers can render the same
 * red blinking badge across the navigation drawer + dashboard.
 *
 * Cheap (count-only payload). 20s default polling.
 */
export default function useApprovalsCount(role, intervalMs = 20000) {
  const [state, setState] = useState({
    total: 0, issues: 0, extras: 0,
    pending_total: 0, pending_issues: 0, pending_extras: 0,
    decided_24h_total: 0,
  });

  useEffect(() => {
    if (role !== "client" && role !== "admin") return undefined;
    let alive = true;
    async function tick() {
      try {
        const url = role === "client"
          ? "/client/approvals/count"
          : "/admin/approvals/count";
        const { data } = await api.get(url);
        if (!alive) return;
        if (role === "client") {
          setState({
            total: data.total || 0,
            issues: data.issues || 0,
            extras: data.extras || 0,
            pending_total: data.total || 0,
            pending_issues: data.issues || 0,
            pending_extras: data.extras || 0,
            decided_24h_total: 0,
          });
        } else {
          setState({
            total: data.pending_total || 0,
            issues: data.pending_issues || 0,
            extras: data.pending_extras || 0,
            pending_total: data.pending_total || 0,
            pending_issues: data.pending_issues || 0,
            pending_extras: data.pending_extras || 0,
            decided_24h_total: data.decided_24h_total || 0,
          });
        }
      } catch {
        /* fail-quiet — badge keeps last known value */
      }
    }
    tick();
    const id = setInterval(tick, intervalMs);
    return () => { alive = false; clearInterval(id); };
  }, [role, intervalMs]);

  return state;
}
